import { createSlice, PayloadAction } from '@reduxjs/toolkit';

type User = {
  id?: string;
  name?: string;
  // keep loose to align with existing backend member shape
  [key: string]: any;
} | null;

interface AuthState {
  isAuthenticated: boolean;
  user: User;
  token: string | null;
}

// Load user data from localStorage on initialization
const loadUserFromStorage = (): User => {
  try {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      return JSON.parse(userStr);
    }
  } catch (error) {
    console.error('Failed to load user from localStorage:', error);
  }
  return null;
};

const loadTokenFromStorage = (): string | null => {
  try {
    return localStorage.getItem('token');
  } catch (error) {
    console.error('Failed to load token from localStorage:', error);
  }
  return null;
};

const initialState: AuthState = {
  isAuthenticated: !!loadTokenFromStorage(),
  user: loadUserFromStorage(),
  token: loadTokenFromStorage(),
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    // Simple local login helper (not used by api.ts)
    login(state, action: PayloadAction<User>) {
      state.isAuthenticated = !!action.payload;
      state.user = action.payload;
    },
    // Used by api.ts: accepts { token, member }
    setCredentials(
      state,
      action: PayloadAction<{ token: string; member: any }>
    ) {
      const { token, member } = action.payload;
      state.token = token;
      state.user = member;
      state.isAuthenticated = true;
    },
    logout(state) {
      state.isAuthenticated = false;
      state.user = null;
      state.token = null;
    },
    updateMemberStatus(state, action: PayloadAction<string>) {
      if (state.user) {
        state.user = { ...state.user, status: action.payload };
        try {
          const stored = localStorage.getItem('user');
          if (stored) {
            localStorage.setItem(
              'user',
              JSON.stringify({ ...JSON.parse(stored), status: action.payload })
            );
          }
        } catch {}
      }
    },
  },
});

export const { login, logout, setCredentials, updateMemberStatus } = authSlice.actions;

export default authSlice.reducer;

